import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Phone, Copy, AlertTriangle, ChevronLeft, PhoneOff, PhoneMissed, WifiOff, Power, ShieldAlert, Voicemail, Check, Settings, PhoneCall } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { NumberedSteps, Note } from '../components/activation/ActivationPrimitives';
import { useTheme } from '../contexts/ThemeContext';
import { Condition, DETECTED_CARRIER, ForwardingSetup, conditionToRule, detectOS, getCarrier } from '../data/mobileForwarding';
export type { Condition };
const CONDITIONS: {
  id: Condition;
  title: string;
  shortTitle: string;
  phrase: string;
  icon: React.ElementType;
}[] = [{
  id: 'busy',
  title: 'When my phone is busy',
  shortTitle: '"When Busy"',
  phrase: 'busy',
  icon: PhoneOff
}, {
  id: 'unanswered',
  title: "When I don't answer",
  shortTitle: '"When Unanswered"',
  phrase: 'unanswered',
  icon: PhoneMissed
}, {
  id: 'unreachable',
  title: "When my phone isn't reachable",
  shortTitle: '"When Unreachable"',
  phrase: 'unreachable',
  icon: WifiOff
}, {
  id: 'off',
  title: 'When my phone is turned off',
  shortTitle: '"When Phone Off"',
  phrase: 'turned off',
  icon: Power
}, {
  id: 'all',
  title: 'Answer All Incoming Calls',
  shortTitle: '"All Calls"',
  phrase: 'ringing',
  icon: Phone
}];

// Names the Android Phone app typically uses for each forwarding option.
const ANDROID_OPTION: Record<string, string> = {
  all: 'Always forward',
  busy: 'Forward when busy',
  unanswered: 'Forward when unanswered',
  unreachable: 'Forward when unreachable'
};
interface IosDeactivationFlowProps {
  conditionId: Condition;
  isLastActiveRule: boolean;
  onComplete: (deactivatedCondition: Condition) => void;
  onEnableAnotherRule: () => void;
  onCancelSubscription: () => void;
  onKeepSubscription: () => void;
  finishAfterDeactivation?: boolean;
  onBack: () => void;
  /** How forwarding was set up — decides between a carrier code and settings steps. */
  setup?: ForwardingSetup | null;
}
export function IosDeactivationFlow({
  conditionId,
  isLastActiveRule,
  onComplete,
  onEnableAnotherRule,
  onCancelSubscription,
  onKeepSubscription,
  finishAfterDeactivation = false,
  onBack,
  setup
}: IosDeactivationFlowProps) {
  const {
    isLight
  } = useTheme();
  const [step, setStep] = useState(1);
  const [verificationState, setVerificationState] = useState<'idle' | 'verifying' | 'troubleshoot'>('idle');
  const [copied, setCopied] = useState(false);
  const [blanketAck, setBlanketAck] = useState(false);
  const currentCondition = CONDITIONS.find(c => c.id === conditionId) || CONDITIONS[0];
  const carrier = getCarrier(setup?.carrierId ?? DETECTED_CARRIER);
  const os = setup?.os ?? detectOS();
  const rule = conditionToRule(conditionId);
  const carrierCode = carrier.codes[rule];
  // Both methods are offered; default to how it was set up. No verified code → settings only.
  const [method, setMethod] = useState<'settings' | 'code'>(setup?.method === 'settings' || !carrierCode ? 'settings' : 'code');
  const useSettings = method === 'settings' || !carrierCode;
  const blanket = Boolean(carrierCode?.deactivateIsBlanket);
  const handleCopy = (text: string) => {
    navigator.clipboard?.writeText(text).catch(() => undefined);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleDeactivationSuccess = () => {
    if (finishAfterDeactivation) {
      setStep(5);
      return;
    }
    if (isLastActiveRule) setStep(3);else onComplete(conditionId);
  };
  const fade = {
    initial: {
      opacity: 0,
      y: 10
    },
    animate: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      y: -10
    }
  };
  const slide = {
    initial: {
      opacity: 0,
      x: 20
    },
    animate: {
      opacity: 1,
      x: 0
    },
    exit: {
      opacity: 0,
      x: -20
    }
  };
  const B = ({
    children
  }: {
    children: React.ReactNode;
  }) => <span className={`font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2423" data-mp="7iffwlvv12ih">{children}</span>;
  const settingsSteps = os === 'ios' ? [<>Open iPhone <B data-id="element-2424" data-mp="295ibq1r8uz1c">Settings</B>.</>, <>Go to <B data-id="element-2425" data-mp="249jkm6ej72x8">Apps → Phone → Call Forwarding</B>.</>, <>If you have two lines, choose the one you set up with Vexu.</>, <>Turn <B data-id="element-2426" data-mp="2iyz89dirms8p">Call Forwarding</B> off.</>, <>Come back to Vexu.</>] : [<>Open the <B data-id="element-2427" data-mp="2e5msztzz7xsm">Phone</B> app, tap the <B data-id="element-2428" data-mp="2rychc52z9esw">three-dot menu</B>, then <B data-id="element-2429" data-mp="2rxl05za4njm5">Settings</B>.</>, <>
            Open <B data-id="element-2430" data-mp="2s0fhvj1raqbv">{setup?.androidBrand === 'samsung' ? 'Supplementary services' : 'Calls'}</B>, then{' '}
            <B data-id="element-2431" data-mp="2nytydeupvdsp">Call forwarding</B>.
          </>, <>If asked, pick the SIM you set up with Vexu, then <B data-id="element-2432" data-mp="31uft82nmsrt1">Voice</B>.</>, <>Tap <B data-id="element-2433" data-mp="2wwxlgflcoimm">{ANDROID_OPTION[rule]}</B> and choose <B data-id="element-2434" data-mp="2wxyxone3rb5x">Turn off</B> or <B data-id="element-2435" data-mp="2wvcoykfh6pm4">Disable</B>.</>, <>Come back to Vexu.</>];
  const voicemailNote = <Note icon={Voicemail} data-id="element-2436" data-mp="17irarqi6y73w">
      If you used voicemail or another forwarding number before Vexu, it may not come back on its own. You can turn it
      back on from your Phone app or by contacting your carrier.
    </Note>;
  const renderStep = () => {
    switch (step) {
      case 1:
        return <motion.div key="step1" {...fade} className="h-full flex flex-col p-6" data-id="element-2437" data-mp="3j7p7pxrf5vqt">
            <button onClick={onBack} className={`absolute top-6 left-6 p-2 rounded-full z-10 ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-400'}`} data-id="element-2438" data-mp="34gop4zwyklgy">
              <ChevronLeft className="w-5 h-5" data-id="element-2439" data-mp="2vicxcukcvhmx" />
            </button>
            <div className="mt-12 mb-8 text-center flex-1 flex flex-col justify-center" data-id="element-2440" data-mp="30g4uc2zuxtxz">
              <div className={`w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center ${isLight ? 'bg-red-50 text-red-600' : 'bg-red-500/10 text-red-400'}`} data-id="element-2441" data-mp="2lje16bgs1egt">
                <currentCondition.icon className="w-10 h-10" data-id="element-2442" data-mp="js0vvlawd4k8" />
              </div>
              <h1 className={`text-2xl font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2443" data-mp="oq2d00oritt2">
                Turn Off AI for {currentCondition.shortTitle}?
              </h1>
              <p className={`text-sm leading-relaxed max-w-xs mx-auto ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2444" data-mp="i90z10v63w1p">
                Your AI will no longer answer your calls when your phone is {currentCondition.phrase}. You can turn
                this back on at any time.
              </p>
            </div>
            <div className="mt-auto space-y-3" data-id="element-2445" data-mp="15zeja57igqlq">
              <Button fullWidth onClick={() => setStep(2)} data-id="element-2446" data-mp="16195rnyfkqrx">
                Continue
              </Button>
              <button onClick={onBack} className={`w-full py-3 text-sm font-medium transition-colors ${isLight ? 'text-slate-500 hover:text-slate-700' : 'text-zinc-400 hover:text-zinc-200'}`} data-id="element-2447" data-mp="112ebnpv0jfsi">
                Cancel
              </button>
            </div>
          </motion.div>;
      case 2:
        return <motion.div key="step2" {...slide} className="h-full flex flex-col p-6 overflow-y-auto pb-24" data-id="element-2448" data-mp="1jxcuj1qjc6x0">
            <button onClick={() => verificationState === 'idle' ? setStep(1) : setVerificationState('idle')} className={`absolute top-6 left-6 p-2 rounded-full z-10 ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-400'}`} data-id="element-2449" data-mp="32niclpyxnyfw">
              <ChevronLeft className="w-5 h-5" data-id="element-2450" data-mp="3gm89p5lr5y49" />
            </button>
            <div className="mt-12 mb-8 text-center" data-id="element-2451" data-mp="3ll1qb6wc658n">
              <h1 className={`text-2xl font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2452" data-mp="3lifj58dcxrqy">
                Updating Your Phone
              </h1>
              <div className="flex items-center gap-2 max-w-[120px] mx-auto" data-id="element-2453" data-mp="3vbe6yo115e9i">
                <div className="h-1.5 flex-1 rounded-full bg-vexu" data-id="element-2454" data-mp="3727i9cadlm7" />
              </div>
            </div>

            {verificationState === 'idle' && carrierCode && <div role="radiogroup" aria-label="Turn off method" className={`mb-6 grid grid-cols-2 gap-1 rounded-2xl p-1 ${isLight ? 'bg-slate-200/60' : 'bg-white/[0.06]'}`} data-id="element-2455" data-mp="83sm0s8g095i">
                {[{
              id: 'settings' as const,
              label: 'Phone settings',
              icon: Settings
            }, {
              id: 'code' as const,
              label: 'Carrier code',
              icon: PhoneCall
            }].map(({
              id,
              label,
              icon: Icon
            }) => {
              const selected = method === id;
              return <button key={id} type="button" role="radio" aria-checked={selected} onClick={() => setMethod(id)} className={`flex items-center justify-center gap-2 whitespace-nowrap rounded-xl py-2.5 text-[13px] font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${selected ? isLight ? 'bg-white text-slate-900 shadow-sm' : 'bg-white/10 text-white' : isLight ? 'text-slate-500 hover:text-slate-700' : 'text-zinc-400 hover:text-zinc-200'}`} data-id="element-2456" data-mp="31h90cud95ak7">
                      <Icon className={`h-4 w-4 ${selected ? isLight ? 'text-vexu-700' : 'text-vexu' : ''}`} data-id="element-2457" data-mp="28e6kyqjwmael" />
                      {label}
                    </button>;
            })}
              </div>}

            <AnimatePresence mode="wait" data-id="element-2458" data-mp="1yod4pc8th740">
              {verificationState === 'idle' && useSettings && <motion.div key="idle-settings" {...fade} data-id="element-2459" data-mp="2me7gdqhegpmo">
                  <h2 className={`text-lg font-bold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2460" data-mp="2rcshds4h7dc6">
                    Turn off forwarding in {os === 'ios' ? 'iPhone Settings' : 'your Phone app'}
                  </h2>
                  <p className={`text-sm mb-5 ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2461" data-mp="2hcryu8efl191">
                    {setup?.method === 'settings' ? 'You set up Vexu from your phone settings, so turning it off there is easiest.' : 'Follow these steps, then come back to Vexu.'}
                  </p>
                  <NumberedSteps steps={settingsSteps} data-id="element-2462" data-mp="317xpiykhubyq" />
                  {os === 'ios' && rule !== 'all' && <Note icon={AlertTriangle} tone="amber" data-id="element-2463" data-mp="2mjkvw88vp57e">
                      iPhone Settings turns off forwarding for every call at once.
                    </Note>}
                  {voicemailNote}
                  <Button fullWidth onClick={() => setVerificationState('verifying')} data-id="element-2464" data-mp="2dhbo3q4zvpxp">
                    I've turned it off
                  </Button>
                </motion.div>}

              {verificationState === 'idle' && !useSettings && carrierCode && <motion.div key="idle-code" {...fade} className="space-y-6" data-id="element-2465" data-mp="28iqlxl66mw6m">
                  <div className={`p-5 rounded-3xl ${isLight ? 'bg-white border border-slate-200 shadow-sm' : 'bg-white/5 border border-white/10'}`} data-id="element-2466" data-mp="1ssntgndr4vm">
                    <h2 className={`text-lg font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2467" data-mp="6pj341rd1wfz">
                      {currentCondition.title}
                    </h2>
                    <p className={`text-sm mb-6 ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2468" data-mp="1mco0qwnd89u4">
                      This code stops forwarding these calls to Vexu.
                    </p>
                    <label className={`text-xs font-bold uppercase tracking-wider mb-1.5 block ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-2469" data-mp="1w7prty17blzc">
                      Deactivation code
                    </label>
                    <div className={`flex items-center justify-between p-3 rounded-xl border ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-black/20 border-white/10'}`} data-id="element-2470" data-mp="2jyv8gfeyuiap">
                      <code className={`text-base font-mono font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2471" data-mp="plnboi7fjwmf">
                        {carrierCode.deactivate}
                      </code>
                      <button onClick={() => handleCopy(carrierCode.deactivate)} aria-label="Copy deactivation code" className={`p-2 rounded-lg transition-colors ${copied ? 'bg-vexu/10 text-vexu' : isLight ? 'bg-white border border-slate-200 text-slate-500' : 'bg-white/10 text-zinc-400'}`} data-id="element-2472" data-mp="2kosa58jyxpbv">
                        {copied ? <CheckCircle2 className="w-4 h-4" data-id="element-2473" data-mp="33ljs9enrxyec" /> : <Copy className="w-4 h-4" data-id="element-2474" data-mp="33kig16v0v5v1" />}
                      </button>
                    </div>
                  </div>

                  {blanket && <button type="button" role="checkbox" aria-checked={blanketAck} onClick={() => setBlanketAck(v => !v)} className={`flex w-full items-start gap-3 rounded-xl border p-4 text-left ${isLight ? 'border-amber-200 bg-amber-50' : 'border-amber-500/20 bg-amber-500/10'}`} data-id="element-2475" data-mp="3ddph4fiqbzpb">
                      <span className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 ${blanketAck ? 'border-vexu bg-vexu' : isLight ? 'border-amber-300 bg-white' : 'border-amber-400/40'}`} data-id="element-2476" data-mp="5caz0l415l64">
                        {blanketAck && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} data-id="element-2477" data-mp="3nho0cb5mx0ko" />}
                      </span>
                      <span className={`text-[13px] leading-relaxed ${isLight ? 'text-amber-900' : 'text-amber-200'}`} data-id="element-2478" data-mp="o0g17o51gy2t">
                        This code turns off <b data-id="element-2479" data-mp="eb68c62dfbdn">all</b> call forwarding on your line, including any
                        other Vexu rules. I understand.
                      </span>
                    </button>}

                  {voicemailNote}

                  <div data-id="element-2480" data-mp="16sij12tf2ken">
                    <Button fullWidth disabled={blanket && !blanketAck} onClick={() => {
                  window.open('tel:' + encodeURIComponent(carrierCode.deactivate), '_self');
                  setVerificationState('verifying');
                }} className="flex items-center justify-center gap-2" data-id="element-2481" data-mp="16ttqbcm2maai">
                      <Phone className="w-4 h-4" data-id="element-2482" data-mp="hjmpoed88722" />
                      Dial Deactivation Code
                    </Button>
                    <p className={`text-xs leading-relaxed mt-3 text-center ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-2483" data-mp="15ai9opbfxxyr">
                      Your Phone app will open with the code ready to dial. Return to Vexu afterwards.
                    </p>
                  </div>
                </motion.div>}

              {verificationState === 'verifying' && <motion.div key="verifying" {...fade} className="space-y-6" data-id="element-2484" data-mp="1o7t8y9x2ore7">
                  <div className="text-center" data-id="element-2485" data-mp="1sz1id5yekcrd">
                    <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-2486" data-mp="1szut0i0hfmes">
                      <CheckCircle2 className="w-8 h-8" data-id="element-2487" data-mp="38pu94yvxxajp" />
                    </div>
                    <h2 className={`text-xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2488" data-mp="3ndylll4egpu1">
                      {useSettings ? 'Is call forwarding off?' : 'Did your phone confirm it?'}
                    </h2>
                    <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2489" data-mp="2usowuh5mvfg0">
                      {useSettings ? 'Check that forwarding is switched off and no longer shows your Vexu number.' : 'Check the message shown after dialling the code.'}
                    </p>
                  </div>
                  {!useSettings && <div className="grid grid-cols-2 gap-2" data-id="element-2490" data-mp="k0e1ydq2b0kd">
                      {['Call Forwarding Disabled', 'Deactivation Successful', 'Service Removed', 'Registration Cancelled'].map(item => <div key={item} className={`rounded-xl border px-3 py-2.5 text-[13px] font-medium ${isLight ? 'border-slate-200 bg-white text-slate-700' : 'border-white/10 bg-white/5 text-zinc-200'}`} data-id="element-2491" data-mp="r180ukvteydk">
                            {item}
                          </div>)}
                    </div>}
                  <div className="space-y-3 pt-1" data-id="element-2492" data-mp="895jf7bcne29">
                    <Button fullWidth onClick={handleDeactivationSuccess} data-id="element-2493" data-mp="da2xb7pbtlxy">
                      {useSettings ? "Yes, it's off" : 'Yes, it was successful'}
                    </Button>
                    <Button fullWidth variant="secondary" onClick={() => setVerificationState('idle')} data-id="element-2494" data-mp="3grl6pjkqz10h">
                      Try again
                    </Button>
                    <button onClick={() => setVerificationState('troubleshoot')} className={`w-full text-sm font-medium pt-1 ${isLight ? 'text-slate-500 hover:text-slate-700' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-2495" data-mp="3lo1si74h53ae">
                      Having trouble?
                    </button>
                  </div>
                </motion.div>}

              {verificationState === 'troubleshoot' && <motion.div key="troubleshoot" {...fade} className="space-y-6 pt-4" data-id="element-2496" data-mp="1jxqr47vcezj6">
                  <div className="text-center mb-6" data-id="element-2497" data-mp="1f4nufqv9r9yr">
                    <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${isLight ? 'bg-red-50 text-red-600' : 'bg-red-500/10 text-red-400'}`} data-id="element-2498" data-mp="1b3vlkyqb7730">
                      <AlertTriangle className="w-8 h-8" data-id="element-2499" data-mp="1167s0c4fw5mf" />
                    </div>
                    <h2 className={`text-xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2500" data-mp="w6v3lmxx6qhq">
                      Deactivation Didn't Work
                    </h2>
                    <p className={`text-sm ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2501" data-mp="2kyy266wbomjy">
                      We couldn't confirm the change. This may happen when:
                    </p>
                  </div>
                  <ul className={`text-sm space-y-3 p-5 rounded-2xl ${isLight ? 'bg-slate-50 text-slate-700' : 'bg-white/5 text-zinc-300'}`} data-id="element-2502" data-mp="2g1fqi8qx7t89">
                    {['The request was cancelled or didn’t finish.', useSettings ? 'The wrong line or option was changed.' : 'Your plan needs a different code.', 'Your mobile plan handles forwarding differently.'].map(t => <li key={t} className="flex items-start gap-2" data-id="element-2503" data-mp="1ie7b8vhijgep">
                        <span className="text-vexu mt-0.5" data-id="element-2504" data-mp="1ig0hi9o7rym6">•</span> {t}
                      </li>)}
                  </ul>
                  {!useSettings && carrier.wifiCallingTip && <Note icon={WifiOff} tone="amber" data-id="element-2505" data-mp="13t1ucqwe3qd9">
                      {carrier.wifiCallingTip}
                    </Note>}
                  <div className="space-y-3 pt-4" data-id="element-2506" data-mp="1dqfxxpx7sko5">
                    <Button fullWidth onClick={() => setVerificationState('idle')} data-id="element-2507" data-mp="1dn1vkhojwbgp">
                      Try Again
                    </Button>
                    <Button fullWidth variant="secondary" data-id="element-2508" data-mp="1mpb7nbzgwcw7">
                      Contact Support
                    </Button>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </motion.div>;
      case 3:
        return <motion.div key="step3" {...slide} className="h-full flex flex-col p-6 overflow-y-auto pb-24" data-id="element-2509" data-mp="1rgb1u9h7cgv0">
            <div className="mt-12 mb-8 text-center" data-id="element-2510" data-mp="26dagghah19yz">
              <div className={`w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center ${isLight ? 'bg-amber-50 text-amber-600' : 'bg-amber-500/10 text-amber-400'}`} data-id="element-2511" data-mp="26bzf0o4i0eb1">
                <ShieldAlert className="w-10 h-10" data-id="element-2512" data-mp="2dzjrppw7hy2x" />
              </div>
              <h1 className={`text-2xl font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2513" data-mp="28ye0xxm3wkw1">
                Your AI is no longer answering your calls
              </h1>
              <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2514" data-mp="2iy4wm2mel5xp">
                All AI answering rules have been turned off. Your subscription is still active, but your AI assistant
                won't answer any calls until you enable at least one answering rule again.
              </p>
            </div>
            <div className="mt-auto space-y-4" data-id="element-2515" data-mp="1z8ugwomnqkzl">
              <div className="space-y-1" data-id="element-2516" data-mp="1z815hacr77b4">
                <p className={`text-xs font-bold uppercase tracking-wider text-center mb-2 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2517" data-mp="1z6zs2z89m8q8">
                  Recommended
                </p>
                <Button fullWidth onClick={onKeepSubscription} data-id="element-2518" data-mp="17fk719mcwu24">
                  Keep My Subscription
                </Button>
                <p className={`text-xs text-center mt-2 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2519" data-mp="1246i0gm9s8at">
                  I'll enable my AI again later.
                </p>
              </div>
              <div className="pt-4 space-y-3" data-id="element-2520" data-mp="sedg67bxj7jr">
                <Button fullWidth variant="secondary" onClick={onEnableAnotherRule} data-id="element-2521" data-mp="sf502kw5zgu7">
                  Enable an Answering Rule
                </Button>
                <button onClick={() => setStep(4)} className={`w-full py-3 text-sm font-medium transition-colors ${isLight ? 'text-red-500 hover:text-red-600' : 'text-red-400 hover:text-red-300'}`} data-id="element-2522" data-mp="1l30u1hucmuna">
                  Cancel Subscription
                </button>
              </div>
            </div>
          </motion.div>;
      case 4:
        return <motion.div key="step4" {...fade} className="h-full flex flex-col p-6 items-center justify-center text-center" data-id="element-2523" data-mp="3vvlwgmr0dzcd">
            <h2 className={`text-2xl font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2524" data-mp="22bbkz5p1tisf">
              Cancel Your Subscription?
            </h2>
            <p className={`text-sm mb-8 leading-relaxed max-w-xs ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2525" data-mp="1smjvr5bdhcl7">
              Cancelling your subscription will stop future renewals and disable your AI assistant until you subscribe
              again.
            </p>
            <div className="w-full space-y-3" data-id="element-2526" data-mp="1imj9pbm4cidb">
              <Button fullWidth onClick={() => setStep(3)} data-id="element-2527" data-mp="1emsl9ek2eeby">
                Keep Subscription
              </Button>
              <Button fullWidth variant="secondary" onClick={() => setStep(5)} data-id="element-2528" data-mp="19oqs20sxcymr">
                <span className="text-red-500" data-id="element-2529" data-mp="14pnzhfu4skvo">Cancel Subscription</span>
              </Button>
            </div>
          </motion.div>;
      case 5:
        return <motion.div key="step5" initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="h-full flex flex-col items-center justify-center p-6 text-center" data-id="element-2530" data-mp="2z35ci8wwtzcg">
            <div className={`w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center ${isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/10 text-zinc-400'}`} data-id="element-2531" data-mp="3bk6nxozrz0jf">
              <CheckCircle2 className="w-8 h-8" data-id="element-2532" data-mp="3larld93ppz0r" />
            </div>
            <h1 className={`text-2xl font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2533" data-mp="2no2fgo5w329m">
              Subscription Cancelled
            </h1>
            <p className={`text-sm leading-relaxed mb-8 max-w-xs ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2534" data-mp="2soq1swj6auxi">
              Your subscription has been cancelled successfully. You can reactivate your AI assistant at any time by
              subscribing again.
            </p>
            <Button fullWidth onClick={onCancelSubscription} data-id="element-2535" data-mp="cuzg0keqtfbk">
              Return to Home
            </Button>
          </motion.div>;
    }
  };
  return <div className={`h-full w-full relative overflow-hidden ${isLight ? 'bg-stone-100' : 'bg-black'}`} data-id="element-2536" data-mp="ffdzkrqv6p70">
      <AnimatePresence mode="wait" data-id="element-2537" data-mp="bf3uriet64tq">{renderStep()}</AnimatePresence>
    </div>;
}